/**
 * useAccessibleTeams (issue #649)
 *
 * Returns the Teams the current user may assign a Service to: every team for
 * Administrators, otherwise only teams the user is a member of (`user.teams`).
 * This mirrors the backend `canAssignServiceToTeam` ABAC rule used for the
 * claim/assign-team operation — the closest existing "which teams can I
 * operate on" authorization concept in the platform, since Service *create*
 * itself carries no team-scoping on the backend today (see
 * `documents/specs/authentication-authorization-spec.md`, ServiceEditor
 * global-write note).
 *
 * `pinnedTeamId` (the entity's current teamId, in edit mode) is always kept
 * in the returned list even when the user is not a member, so editing a
 * service they are otherwise allowed to edit (as ServiceEditor)
 * never hides its existing team assignment from the dropdown. The user still
 * cannot pick an arbitrary *other* out-of-scope team — reassignment to a team
 * they don't belong to remains an Administrator-only / dedicated-workflow
 * action, per the auth spec's "Team Ownership Rules".
 */
import { useSelector } from 'react-redux';
import type { QueryResult } from '@apollo/client/react';
import { useAllTeams } from '../graphql/queries';
import { selectUser } from '../store/features/auth/authSlice';
import { canAssignServiceToTeam } from '../lib/permissions';
import type { Team, TeamConnection } from '../graphql/types';

export interface UseAccessibleTeamsResult {
  /** Teams the user may select, sorted by name. */
  teams: Team[];
  /** True while the underlying (fully-paginated) teams query is in flight. */
  loading: boolean;
  /** Apollo error from the underlying teams query, if any. */
  error: QueryResult<{ teams: TeamConnection }>['error'];
}

export function useAccessibleTeams(pinnedTeamId?: string | null): UseAccessibleTeamsResult {
  const user = useSelector(selectUser);
  const { data, loading, error } = useAllTeams();

  const allTeams = data?.teams?.edges?.map((edge) => edge.node) ?? [];
  const teams = allTeams
    .filter((team) => team.entityId === pinnedTeamId || canAssignServiceToTeam(user, team.entityId))
    .sort((a, b) => a.name.localeCompare(b.name));

  return { teams, loading, error };
}
